import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { candidateService } from '../services/candidateService';
import MainLayout from '../components/Layout/MainLayout';
import CandidateDetailsModal from '../components/CandidateDetailsModal';

const CandidatesListPage = () => {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);
  const [selectedCandidateIndex, setSelectedCandidateIndex] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [filters, setFilters] = useState({
    name: '',
    passportNumber: '',
    jobRole: '',
    mobile: ''
  });

  // Debounce timer ref
  const debounceRef = useRef(null);
  // Track current page + filters for fetch
  const pageRef = useRef(1);
  const filtersRef = useRef(filters);

  const fetchCandidates = useCallback(async (page, currentFilters) => {
    try {
      setLoading(true);
      setError('');
      const response = await candidateService.getAllCandidates(page, 20, currentFilters);
      setCandidates(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError('Failed to load candidates');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchCandidates(1, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (e) => {
    const updated = { ...filters, [e.target.name]: e.target.value };
    setFilters(updated);
    filtersRef.current = updated;
    pageRef.current = 1;

    // Debounce API call by 400ms
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchCandidates(1, updated);
    }, 400);
  };

  const handleClearSearch = () => {
    const empty = { name: '', passportNumber: '', jobRole: '', mobile: '' };
    setFilters(empty);
    filtersRef.current = empty;
    pageRef.current = 1;
    clearTimeout(debounceRef.current);
    fetchCandidates(1, empty);
  };

  const handlePageChange = (newPage) => {
    pageRef.current = newPage;
    setPagination(prev => ({ ...prev, page: newPage }));
    fetchCandidates(newPage, filtersRef.current);
  };

  const viewCandidateDetails = (candidateId, index) => {
    setSelectedCandidateId(candidateId);
    setSelectedCandidateIndex(index);
  };

  const handleNextCandidate = () => {
    if (selectedCandidateIndex < candidates.length - 1) {
      const nextIndex = selectedCandidateIndex + 1;
      setSelectedCandidateId(candidates[nextIndex]._id);
      setSelectedCandidateIndex(nextIndex);
    }
  };

  const handlePreviousCandidate = () => {
    if (selectedCandidateIndex > 0) {
      const prevIndex = selectedCandidateIndex - 1;
      setSelectedCandidateId(candidates[prevIndex]._id);
      setSelectedCandidateIndex(prevIndex);
    }
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  const rightSidebarContent = (
    <div className="candidates-stats">
      <h3>Statistics</h3>
      <div className="stat-item">
        <span className="stat-label">Total Candidates</span>
        <span className="stat-value">{pagination.total}</span>
      </div>
      <div className="stat-item">
        <span className="stat-label">Current Page</span>
        <span className="stat-value">{pagination.page} of {pagination.pages}</span>
      </div>
      <div className="stat-item">
        <span className="stat-label">Showing</span>
        <span className="stat-value">{candidates.length} candidates</span>
      </div>
    </div>
  );

  return (
    <MainLayout rightSidebarContent={rightSidebarContent}>
      <div className="max-w-[1400px] mx-auto p-8 lg:p-4">
        <div className="flex justify-between items-center mb-8 lg:flex-col lg:items-start lg:gap-4">
          <h1 className="text-primary text-[2rem] m-0 lg:text-2xl">Candidates</h1>
        </div>

        {error && <div className="bg-[#fee] text-[#c33] p-4 rounded-lg mb-6 border-l-4 border-[#c33]">{error}</div>}

        <div className="bg-white p-6 rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.1)] mb-8 lg:p-4">
          <h2 className="text-primary text-[1.3rem] mb-4">Search Candidates</h2>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4 mb-4 lg:grid-cols-1">
            <input
              type="text"
              name="name"
              placeholder="Search by name..."
              value={filters.name}
              onChange={handleFilterChange}
              className="w-full p-3 border-2 border-gray-300 rounded-lg text-base transition-all duration-300 focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]"
            />
            <input
              type="text"
              name="passportNumber"
              placeholder="Passport number..."
              value={filters.passportNumber}
              onChange={handleFilterChange}
              className="w-full p-3 border-2 border-gray-300 rounded-lg text-base transition-all duration-300 focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]"
            />
            <input
              type="text"
              name="jobRole"
              placeholder="Job role..."
              value={filters.jobRole}
              onChange={handleFilterChange}
              className="w-full p-3 border-2 border-gray-300 rounded-lg text-base transition-all duration-300 focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]"
            />
            <input
              type="text"
              name="mobile"
              placeholder="Mobile number..."
              value={filters.mobile}
              onChange={handleFilterChange}
              className="w-full p-3 border-2 border-gray-300 rounded-lg text-base transition-all duration-300 focus:outline-none focus:border-accent-green focus:shadow-[0_0_0_3px_rgba(107,127,92,0.1)]"
            />
          </div>
          {hasActiveFilters && (
            <button
              className="py-3 px-8 border-none rounded-lg text-base cursor-pointer transition-all duration-300 font-semibold bg-gray-100 text-primary hover:bg-gray-300"
              onClick={handleClearSearch}
            >
              Clear Filters
            </button>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.1)] overflow-hidden">
          {loading ? (
            <div className="text-center p-12 text-primary text-[1.1rem]">Loading candidates...</div>
          ) : candidates.length === 0 ? (
            <div className="text-center p-12 text-primary text-[1.1rem]">
              {hasActiveFilters ? 'No candidates match your search' : 'No candidates found'}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead className="bg-background">
                    <tr>
                      <th className="p-4 text-left font-semibold text-primary text-[0.9rem] uppercase tracking-wide">Name</th>
                      <th className="p-4 text-left font-semibold text-primary text-[0.9rem] uppercase tracking-wide">Passport Number</th>
                      <th className="p-4 text-left font-semibold text-primary text-[0.9rem] uppercase tracking-wide">Job Role</th>
                      <th className="p-4 text-left font-semibold text-primary text-[0.9rem] uppercase tracking-wide">Phone</th>
                      <th className="p-4 text-left font-semibold text-primary text-[0.9rem] uppercase tracking-wide">Email</th>
                      <th className="p-4 text-left font-semibold text-primary text-[0.9rem] uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidates.map((candidate, index) => (
                      <tr key={candidate._id} className="border-b border-gray-100 transition-colors duration-200 hover:bg-gray-50">
                        <td className="p-4 text-primary">
                          <div className="font-semibold text-primary">
                            {candidate.personalInformation?.name || `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim() || 'N/A'}
                          </div>
                        </td>
                        <td className="p-4 text-primary">
                          {candidate.personalInformation?.passportNumber || candidate.passportNumber || '-'}
                        </td>
                        <td className="p-4 text-primary">
                          {candidate.professionalDetails?.currentJobRole || candidate.jobRole || '-'}
                        </td>
                        <td className="p-4 text-primary">
                          {candidate.personalInformation?.mobile || candidate.userId?.phoneNumber || '-'}
                        </td>
                        <td className="p-4 text-primary">{candidate.userId?.email || '-'}</td>
                        <td className="p-4 text-primary">
                          <button
                            className="bg-accent-green text-white border-none py-2 px-4 rounded-md cursor-pointer text-[0.9rem] transition-all duration-300 hover:bg-[#5a6e4d] hover:-translate-y-0.5"
                            onClick={() => viewCandidateDetails(candidate._id, index)}
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {pagination.pages > 1 && (
                <div className="flex justify-center items-center gap-4 p-6 border-t border-gray-100">
                  <button
                    className="bg-accent-green text-white border-none py-2 px-6 rounded-md cursor-pointer text-[0.9rem] transition-all duration-300 hover:bg-[#5a6e4d] disabled:bg-gray-400 disabled:cursor-not-allowed"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                  >
                    Previous
                  </button>
                  <span className="text-primary font-semibold">
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  <button
                    className="bg-accent-green text-white border-none py-2 px-6 rounded-md cursor-pointer text-[0.9rem] transition-all duration-300 hover:bg-[#5a6e4d] disabled:bg-gray-400 disabled:cursor-not-allowed"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.pages}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {selectedCandidateId && (
          <CandidateDetailsModal
            candidateId={selectedCandidateId}
            onClose={() => {
              setSelectedCandidateId(null);
              setSelectedCandidateIndex(null);
            }}
            onNext={handleNextCandidate}
            onPrevious={handlePreviousCandidate}
            hasNext={selectedCandidateIndex < candidates.length - 1}
            hasPrevious={selectedCandidateIndex > 0}
          />
        )}
      </div>
    </MainLayout>
  );
};

export default CandidatesListPage;
